import React, { useEffect, useRef, useState } from "react";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import { getQuarryColumns } from "./constants";
import { appRoutes } from "../../../constants/appRoutes";
import { generatePath, useHistory } from "react-router-dom";
import QuarryDatabaseService from "../../../services/quarryDatabase.service";
import { FilterQuarryListParms } from "../../../Types/quarryDatabaseTypes";
import { QuarrySortByColumns } from "../../../enums/QuarryDatabaseEnum";
import { TableSortTypes } from "../../../enums/ViewListingTypes";
import CustomerDatabaseService from "../../../services/customerDatabase.service";
import { ArchiveKeys } from "../../../enums/ArchiveActionKeys";
import { message } from "antd";
import DeleteConfirmation from "../../DatabaseTab/DeleteConfirmation";
import { DatabaseSections } from "../../../enums/DatabaseEnums";
import Form from "../../Form";
import { deleteFormSchema } from "../../DatabaseTab/constants";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../constants/QueryKeys";
import TNLoader from "../../../reusable-components/TNLoader";
import { useQuarryDatabaseContext } from "../../../context/QuarryDatabaseFilterContext";
import { storageKeys } from "../../../constants/storageKeys";
import JobDatabaseService from "../../../services/jobDatabase.service";
import { ApiResponse } from "../../../enums/ApiResponse";

interface QuarryTableViewProps {
  filter: FilterQuarryListParms;
  handleEditNavigate?: (id: string) => void;
  handleViewNavigate?: (record: object) => void;
}

const QuarryTableView = (props?: QuarryTableViewProps) => {
  const isMountRef = useRef<boolean>(true);
  const isFilterMountRef = useRef<boolean>(true);
  const { filter, handleEditNavigate, handleViewNavigate } = props;
  const queryClient = useQueryClient();
  const { GetPaginationDefaultMeta } = JobDatabaseService();
  const { quarryDatabaseFilters, updateQuarryDatabaseFilters } =
    useQuarryDatabaseContext();
  const { address, ...cleanedFilter } = filter;
  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(Number(sessionStorage?.getItem(storageKeys?.QUARRY_DB_PAGESIZE) ?? 10));
  const navigate = useHistory();
  const { FetchQuarryList } = QuarryDatabaseService();
  const { DeleteDatabaseTableEntries } = CustomerDatabaseService();
  const { data: defaultData, isFetching: gettingDefaults } = GetPaginationDefaultMeta();
  const { mutateAsync: deleteQuarry, isPending } = DeleteDatabaseTableEntries();
  const { data, refetch, isFetching, isSuccess: listFetchedSuccess } = FetchQuarryList({
    ...cleanedFilter,
    page: currentPage,
    perPage: pageSize,
    sortBy: quarryDatabaseFilters?.sortBy ?? QuarrySortByColumns?.QUARRY_NAME,
    sortOrder: quarryDatabaseFilters?.sortOrder ?? TableSortTypes?.ASCEND,
  }, pageSizeChanged, !!sessionStorage.getItem(storageKeys.QUARRY_DB_PAGESIZE));

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
    sessionStorage.setItem(
      storageKeys.QUARRY_DB_PAGE,
      JSON.stringify({ page: page }),
    );
  };

  const handlePaginationPageSize = (size: number) => {
    setPageSizeChanged(true);
    setPageSize(size);
    setCurrentPage(1);
    try {
      sessionStorage.setItem(storageKeys.QUARRY_DB_PAGESIZE, size);
    } catch (err) {
      console.error("Err", err);
    }
  };

  const handleSorter = (sorter) => {
    if (!!!sorter?.order) {
      updateQuarryDatabaseFilters({
        sortBy: undefined,
        sortOrder: undefined,
      });
      return;
    }
    updateQuarryDatabaseFilters({
      sortBy: sorter?.columnKey,
      sortOrder: sorter?.order,
    });
  };
  const [isDeleteModalOpen, setDeleteModalopen] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const handleDeleteModalOpen = () => setDeleteModalopen(true);
  const handleDeleteModalClose = () => setDeleteModalopen(false);

  const handleDeleteSelect = (id?: string) => {
    setDeleteId(id);
    handleDeleteModalOpen();
  };

  const handleDelete = async (formVal?: object) => {
    await deleteQuarry(
      {
        id: deleteId,
        deletedTable: ArchiveKeys.QUARRIES,
        notes: formVal?.archiveReason,
      },
      {
        onSuccess: () => {
          message.success("Quarry deleted successfully!");
          handleDeleteModalClose();
          queryClient.invalidateQueries({
            queryKey: [QueryKeys?.FETCH_QUARRY_META_KEY],
          });
          refetch();
        },
        onError: () => {
          message.error("Failed to delete quarry!");
        },
      },
    );
  };

  useEffect(() => {
    const storageItem = sessionStorage.getItem(storageKeys.QUARRY_DB_PAGE);
    if (!!storageItem) {
      const pageValue = JSON.parse(storageItem);
      setCurrentPage(pageValue?.page);
    }
    const storageItemSize = sessionStorage.getItem(
      storageKeys.QUARRY_DB_PAGESIZE,
    );
    if (!!storageItemSize) {
      const pageSizeValue = JSON.parse(storageItemSize);
      setPageSize(pageSizeValue);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [window.location.hash]);

  useEffect(() => {
    if(isFilterMountRef.current) {
      isFilterMountRef.current = false;
      return;
    }
    handleCurrentPage(1);
  }, [
    cleanedFilter?.search,
    cleanedFilter?.category,
    cleanedFilter?.latitude,
    cleanedFilter?.longitude,
    cleanedFilter?.materialType,
    cleanedFilter?.radius,
    quarryDatabaseFilters?.sortBy,
    quarryDatabaseFilters?.sortOrder
  ]);

  useEffect(() => {
    if(
      (!!defaultData?.message && defaultData?.message === ApiResponse.SUCCESS)
    ) {
      setPageSize(defaultData?.data?.quarryDefaultPageSize);
      sessionStorage.setItem(storageKeys.QUARRY_DB_PAGESIZE, defaultData?.data?.quarryDefaultPageSize)
    }
  }, [defaultData])

  useEffect(() => {
    if(listFetchedSuccess && pageSizeChanged) {
      setPageSizeChanged(false);
    }
  }, [listFetchedSuccess, pageSizeChanged])

  return (
    <>
      <TNLoader isLoading={isFetching || gettingDefaults} />
      <Form validationSchema={deleteFormSchema}>
        {({ control: { handleSubmit } }) => (
          <DeleteConfirmation
            tabType={DatabaseSections.QUARRY}
            deletePending={isPending}
            isOpen={isDeleteModalOpen}
            handleClose={handleDeleteModalClose}
            submitActionCall={handleSubmit(handleDelete)}
          />
        )}
      </Form>
      <CustomTableWithPagination
        data={data?.data ?? []}
        columns={getQuarryColumns({
          sortColumn: quarryDatabaseFilters?.sortBy,
          sortOrder: quarryDatabaseFilters?.sortOrder,
          handleEditNavigate: handleEditNavigate,
          handleDeleteSelect: handleDeleteSelect,
        })}
        isClickableTable
        handleSelectedRow={handleViewNavigate}
        handleChange={handleCurrentPage}
        handleSizeChangerChange={handlePaginationPageSize}
        isSortable={true}
        handleSorter={handleSorter}
        totalData={data?.meta?.totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
      />
    </>
  );
};

export default QuarryTableView;
